import {type SafeHTMLString, SafeHTMLText} from '@github-ui/safe-html'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {TagIcon} from '@primer/octicons-react'
import {Box, LinkButton} from '@primer/react'
import type React from 'react'
import {forwardRef, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, readInlineData, usePaginationFragment, useRelayEnvironment, fetchQuery} from 'react-relay'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'

import {VALUES} from '../constants/values'
import {ERRORS} from '../constants/errors'
import {HOTKEYS} from '../constants/hotkeys'
import {LABELS} from '../constants/labels'
import {CompressedLabelsToken} from './CompressedLabelsToken'
import type {LabelPickerLabel$data, LabelPickerLabel$key} from './__generated__/LabelPickerLabel.graphql'
import type {LabelPickerLabelsPaginated$key} from './__generated__/LabelPickerLabelsPaginated.graphql'
import type {LabelPickerQuery} from './__generated__/LabelPickerQuery.graphql'
import type {LabelsListPaginationQuery} from './__generated__/LabelsListPaginationQuery.graphql'
import {type ExtendedItemProps, ItemPicker, type SharedBulkActionsItemPickerProps} from './ItemPicker'
import type {ItemGroup} from '../shared'
import {commitUpdateIssueLabelsBulkMutation} from '../mutations/update-issue-labels-bulk-mutation'
import type {updateIssueLabelsBulkMutation$data} from '../mutations/__generated__/updateIssueLabelsBulkMutation.graphql'
import type {updateIssueLabelsBulkByQueryMutation$data} from '../mutations/__generated__/updateIssueLabelsBulkByQueryMutation.graphql'
import {commitUpdateIssueLabelsBulkByQueryMutation} from '../mutations/update-issue-labels-bulk-by-query-mutation'
import {useItemPickerErrorFallback} from './useItemPickerErrorFallback'
import {useKeyPress} from '@github-ui/use-key-press'
import {IS_SERVER, ssrSafeDocument} from '@github-ui/ssr-utils'
import {fuzzyFilter} from '@github-ui/fuzzy-score/fuzzy-filter'
import type {LabelPickerSearchQuery, LabelPickerSearchQuery$data} from './__generated__/LabelPickerSearchQuery.graphql'
import {URLS} from '../constants/urls'
import {useDebounce} from '@github-ui/use-debounce'

//
// ItemPicker <== ItemPickerWrapper (internal) <== ItemPickerFetcher (internal) <== LabelPicker (exported)
//                                                                              <== BulkLabelPicker (exported)

export type LabelPickerProps = {
  repo: string
  owner: string
  readonly: boolean
  labels: LabelPickerLabel$data[]
  labelNames?: string[]
  insidePortal?: boolean
  shortcutEnabled: boolean
  anchorElement: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element
  onSelectionChanged: (selectedItems: LabelPickerLabel$data[]) => void
  /**
   * Whether to render the label picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  triggerOpen?: boolean
  initialFilter?: string
  title?: string
  shortcutHintVisible?: boolean
  showEditLabelsButton?: boolean
}

type BulkLabelPickerProps = Omit<LabelPickerProps, 'onSelectionChanged' | 'labels'> & {
  existingIssueLabels: Map<string, string[]>
  labelAppliedToAll: LabelPickerLabel$data[]
  connectionIds: {[key: string]: string[]}
} & SharedBulkActionsItemPickerProps

type ItemPickerWrapperProps = Omit<LabelPickerProps, 'labelQueryRef'> & {
  paginatedKey: LabelPickerLabelsPaginated$key | null
  isLoading: boolean
  /**
   * Callback to search for labels on the server.
   *
   * Only used when repository has more than VALUES.labelsMaxPreloadCount
   *
   * @param query filter query
   * @returns Search results
   */
  searchLabels?: (query: string) => Promise<LabelPickerLabel$data[]>
  repoNwo?: string
}

export const LabelPickerGraphqlQuery = graphql`
  query LabelPickerQuery(
    $owner: String!
    $repo: String!
    $shouldQueryByNames: Boolean = false
    $labelNames: String = ""
    $count: Int = 100
  ) {
    repository(owner: $owner, name: $repo) {
      ...LabelPickerLabelsPaginated
      labelsByNames: labels(names: $labelNames, first: $count, orderBy: {field: NAME, direction: ASC})
        @include(if: $shouldQueryByNames) {
        nodes {
          ...LabelPickerLabel
        }
      }
    }
  }
`

const LabelPickerSearchGraphqlQuery = graphql`
  query LabelPickerSearchQuery($owner: String!, $repo: String!, $query: String, $count: Int!) {
    repository(owner: $owner, name: $repo) {
      labels(query: $query, first: $count) {
        edges {
          node {
            ...LabelPickerLabel
          }
        }
      }
    }
  }
`

export const AssignedLabelFragment = graphql`
  fragment LabelPickerAssignedLabels on Labelable
  @argumentDefinitions(cursor: {type: "String"}, assignedLabelPageSize: {type: "Int", defaultValue: 20})
  @refetchable(queryName: "LabelPickerAssignedLabelsQuery") {
    labels(first: $assignedLabelPageSize, after: $cursor, orderBy: {field: NAME, direction: ASC})
      @connection(key: "LabelPicker_labels") {
      edges {
        node {
          ...LabelPickerLabel
        }
      }
    }
  }
`

export const LabelPickerLabelsPaginatedFragment = graphql`
  fragment LabelPickerLabelsPaginated on Repository
  @argumentDefinitions(cursor: {type: "String"}, count: {type: "Int", defaultValue: 100})
  @refetchable(queryName: "LabelsListPaginationQuery") {
    labels(first: $count, after: $cursor, orderBy: {field: NAME, direction: ASC})
      @connection(key: "Repository_labels") {
      edges {
        node {
          ...LabelPickerLabel
        }
      }
      totalCount
    }
  }
`

export const LabelFragment = graphql`
  fragment LabelPickerLabel on Label @inline {
    id
    color
    name
    nameHTML
    description
    # eslint-disable-next-line relay/unused-fields
    url
  }
`

type CompressedLabelAnchorProps = {
  size?: 'small' | 'large'
  labels: LabelPickerLabel$data[]
  anchorProps?: React.HTMLAttributes<HTMLElement>
  displayHotkey: boolean
  MAX_DISPLAYED_LABELS?: number
  readonly?: boolean
}

const CompressedLabelAnchor = forwardRef<HTMLButtonElement, CompressedLabelAnchorProps>(
  ({size = 'small', labels, anchorProps, displayHotkey, MAX_DISPLAYED_LABELS = 2, readonly}, ref) => {
    return (
      <CompressedLabelsToken
        size={size}
        labelNamesWithColor={labels.map(l => ({nameHTML: l.nameHTML, color: l.color}))}
        anchorProps={anchorProps}
        MAX_DISPLAYED_LABELS={MAX_DISPLAYED_LABELS}
        readonly={readonly}
        leadingIcon={TagIcon}
        hotKey={!readonly && displayHotkey ? HOTKEYS.labelPicker.toUpperCase() : undefined}
        anchorText={labels?.length === 0 ? LABELS.noLabels : undefined}
        ref={ref}
      />
    )
  },
)

CompressedLabelAnchor.displayName = 'CompressedLabelAnchor'

const labelsGroup: ItemGroup = {groupId: 'labels'}
const suggestionsGroup: ItemGroup = {groupId: 'suggestions', header: {title: 'Suggestions', variant: 'filled'}}

export function BulkIssuesLabelPicker({
  issuesToActOn,
  existingIssueLabels,
  labelAppliedToAll,
  connectionIds,
  useQueryForAction,
  repositoryId,
  query,
  onCompleted,
  onError,
  ...props
}: BulkLabelPickerProps) {
  const environment = useRelayEnvironment()

  const onSelectionChange = useCallback(
    (selectedLabels: LabelPickerLabel$data[]) => {
      const labelsToAdd = selectedLabels.filter(l => !labelAppliedToAll.some(la => la.id === l.id))
      const labelsToRemove = labelAppliedToAll.filter(la => !selectedLabels.some(l => la.id === l.id))
      const args = {
        applyLabelIds: labelsToAdd.map(l => l.id),
        removeLabelIds: labelsToRemove.map(l => l.id),
      }
      const mutationArgs = {
        environment,
        optimisticUpdateIds: issuesToActOn,
        existingIssueLabels,
        connectionIds,
        onError: (error: Error) => {
          onError?.(error)
        },
      }

      if (useQueryForAction && repositoryId && query) {
        commitUpdateIssueLabelsBulkByQueryMutation({
          ...mutationArgs,
          input: {
            repositoryId,
            query,
            ...args,
          },
          onCompleted: ({updateIssuesBulkByQuery}: updateIssueLabelsBulkByQueryMutation$data) => {
            onCompleted?.(updateIssuesBulkByQuery?.jobId || undefined)
          },
        })
      } else {
        commitUpdateIssueLabelsBulkMutation({
          ...mutationArgs,
          input: {
            ids: [...issuesToActOn],
            ...args,
          },
          onCompleted: ({updateIssuesBulk}: updateIssueLabelsBulkMutation$data) => {
            onCompleted?.(updateIssuesBulk?.jobId || undefined)
          },
        })
      }
    },
    [
      connectionIds,
      environment,
      existingIssueLabels,
      issuesToActOn,
      labelAppliedToAll,
      onCompleted,
      onError,
      query,
      repositoryId,
      useQueryForAction,
    ],
  )

  return (
    <LabelPicker
      labels={labelAppliedToAll}
      onSelectionChanged={onSelectionChange}
      showEditLabelsButton={false}
      {...props}
    />
  )
}

export function LabelPicker({shortcutEnabled, anchorElement, ...rest}: LabelPickerProps) {
  const [wasTriggered, setWasTriggered] = useState(false)

  useKeyPress(shortcutEnabled ? [HOTKEYS.labelPicker] : [], (e: KeyboardEvent) => {
    if (!wasTriggered) {
      setWasTriggered(true)
      e.preventDefault()
    }
  })

  if (!wasTriggered && anchorElement) {
    return anchorElement({onClick: () => setWasTriggered(true)})
  }

  return <ItemPickerFetcher anchorElement={anchorElement} shortcutEnabled={shortcutEnabled} {...rest} />
}

function ItemPickerFetcher({repo, owner, labels, labelNames, onSelectionChanged, ...props}: LabelPickerProps) {
  const environment = useRelayEnvironment()

  const [isLoading, setIsLoading] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<LabelPickerLabelsPaginated$key | null>(null)
  const [initialLabels, setInitialLabels] = useState<LabelPickerLabel$data[]>(labels)
  const repoNwo = `${owner}/${repo}`

  useEffect(() => {
    if (!IS_SERVER) {
      clientSideRelayFetchQueryRetained<LabelPickerQuery>({
        environment,
        query: LabelPickerGraphqlQuery,
        variables: {
          owner,
          repo,
          shouldQueryByNames: labelNames && labelNames.length > 0,
          labelNames: labelNames?.join(','),
          count: VALUES.labelsPageSize,
        },
      }).subscribe({
        next: response => {
          const repoLabels = response.repository
          setData(repoLabels ?? null)
          if (labelNames && response.repository?.labelsByNames) {
            const labelsByName = (response.repository?.labelsByNames.nodes || []).flatMap(node =>
              // eslint-disable-next-line no-restricted-syntax
              node ? [readInlineData<LabelPickerLabel$key>(LabelFragment, node)] : [],
            )
            setInitialLabels(labelsByName)
          }
          setIsLoading(false)
          setIsError(false)
        },
        error: () => {
          setIsError(true)
        },
      })
    }
  }, [environment, fetchKey, labelNames, owner, repo])

  const searchLabels = useCallback(
    (query: string) =>
      new Promise<LabelPickerLabel$data[]>((resolve, reject) => {
        fetchQuery<LabelPickerSearchQuery>(environment, LabelPickerSearchGraphqlQuery, {
          query,
          owner,
          repo,
          count: 10,
        }).subscribe({
          next: (fetchedData: LabelPickerSearchQuery$data) => {
            if (fetchedData !== null) {
              const suggestedLabels = (fetchedData.repository?.labels?.edges || []).flatMap(
                // eslint-disable-next-line no-restricted-syntax
                edge => (edge?.node ? [readInlineData<LabelPickerLabel$key>(LabelFragment, edge.node)] : []),
              )
              resolve(suggestedLabels)
            }
          },
          error: (error: Error) => {
            reject(error)
          },
        })
      }),
    [environment, owner, repo],
  )

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('labels'),
    anchorElement: props.anchorElement,
    open: true,
  })

  if (isError) {
    return createFallbackComponent(() => setFetchKey(fetchKey + 1))
  }

  return (
    <ItemPickerWrapper
      repo={repo}
      owner={owner}
      paginatedKey={data}
      isLoading={isLoading}
      // If we're fetching labels by name, make sure we'll pass here the fetched labels
      // This is a particularity for tests, as we can't rely on the initialLabels default state
      labels={labelNames ? initialLabels : labels}
      onSelectionChanged={onSelectionChanged}
      searchLabels={searchLabels}
      repoNwo={repoNwo}
      {...props}
    />
  )
}

function ItemPickerWrapper({
  labels: selectedLabels,
  searchLabels,
  paginatedKey,
  isLoading,
  onSelectionChanged,
  insidePortal,
  shortcutEnabled,
  anchorElement,
  nested = false,
  triggerOpen = true,
  initialFilter,
  repoNwo,
  showEditLabelsButton = true,
  title,
}: ItemPickerWrapperProps) {
  const [filter, setFilter] = useState('')
  const [isServerFetching, setIsServerFetching] = useState(false)
  const [loadedLabels, setLoadedLabels] = useState<LabelPickerLabel$data[]>([])
  const {addToast} = useToastContext()

  const {data, loadNext, isLoadingNext, hasNext} = usePaginationFragment<
    LabelsListPaginationQuery,
    LabelPickerLabelsPaginated$key
  >(LabelPickerLabelsPaginatedFragment, paginatedKey)
  const totalNumberOfLabels = data?.labels?.totalCount || 0
  const fetchedLabels = (data?.labels?.edges || []).flatMap(a =>
    // eslint-disable-next-line no-restricted-syntax
    a?.node ? [readInlineData<LabelPickerLabel$key>(LabelFragment, a.node)] : [],
  )
  const shouldServerFetch = searchLabels && totalNumberOfLabels > VALUES.labelsMaxPreloadCount

  /**
   * Store the initially preloaded labels from the parent component on loadedLabels
   * to show label suggestions when the user opens the label picker
   */
  useEffect(() => {
    if (loadedLabels.length > 0 || fetchedLabels.length === 0) return

    setLoadedLabels(fetchedLabels)
  }, [fetchedLabels, loadedLabels.length])

  useEffect(() => {
    if (shouldServerFetch || !hasNext || isLoadingNext || fetchedLabels.length >= VALUES.labelsMaxPreloadCount) return

    loadNext(VALUES.labelsPageSize)
  }, [loadNext, totalNumberOfLabels, hasNext, isLoadingNext, shouldServerFetch, fetchedLabels.length])

  const items = useMemo(() => {
    const repositoryLabels = shouldServerFetch
      ? loadedLabels
      : fetchedLabels.slice().sort((a, b) => a.name.localeCompare(b.name))
    const sortedSelectedLabels = selectedLabels.slice().sort((a, b) => a.name.localeCompare(b.name))
    const allLabels = sortedSelectedLabels.concat(
      repositoryLabels.filter(l => !selectedLabels.find(l2 => l2.id === l.id)),
    )
    if (!filter) return allLabels
    return fuzzyFilter<LabelPickerLabel$data>({
      items: allLabels,
      filter,
      key: (l: LabelPickerLabel$data) => l.name,
      secondaryKey: (l: LabelPickerLabel$data) => `${l.name} ${l.description}`,
    })
  }, [fetchedLabels, filter, shouldServerFetch, selectedLabels, loadedLabels])

  const fetchSearchData = useCallback(
    async (query: string) => {
      if (!searchLabels) return
      setIsServerFetching(true)

      if (query === '') {
        setLoadedLabels(fetchedLabels)
        setIsServerFetching(false)
        return
      }

      try {
        setLoadedLabels(await searchLabels(query))
      } catch {
        // Temporarily disabling this until we have a standard way of displaying fetch errors
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotSearchLabels,
        })
      } finally {
        setIsServerFetching(false)
      }
    },
    [addToast, fetchedLabels, searchLabels],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
  )

  const filterItems = useCallback(
    (value: string) => {
      const trimmedFilter = value.trim()
      if (filter !== trimmedFilter && shouldServerFetch) {
        setIsServerFetching(true)
        debounceFetchSearchData(trimmedFilter)
      }

      setFilter(value)
    },
    [debounceFetchSearchData, filter, shouldServerFetch],
  )

  const getItemKey = useCallback((label: LabelPickerLabel$data) => label.id, [])

  const convertToItemProps = useCallback(
    (label: LabelPickerLabel$data): ExtendedItemProps<LabelPickerLabel$data> => {
      return {
        id: label.id,
        description: label.description ?? undefined,
        descriptionVariant: 'block',
        children: <SafeHTMLText sx={{wordBreak: 'break-word'}} html={label.nameHTML as SafeHTMLString} />,
        source: label,
        groupId: selectedLabels.find(l => l.id === label.id) ? labelsGroup.groupId : suggestionsGroup.groupId,
        leadingVisual: () => (
          <Box
            sx={{
              bg: `#${label.color}`,
              borderColor: `#${label.color}`,
              width: 14,
              height: 14,
              borderRadius: 10,
              borderWidth: '1px',
              borderStyle: 'solid',
            }}
          />
        ),
      }
    },
    [selectedLabels],
  )

  const labelPickerRef = useRef<HTMLButtonElement>(null)

  const groups: ItemGroup[] = useMemo(() => {
    const itemGroups = []
    if (selectedLabels.length > 0) {
      itemGroups.push(labelsGroup)
    }
    if (items.length > 0 && items.length - selectedLabels.length > 0) {
      itemGroups.push(suggestionsGroup)
    }
    return itemGroups
  }, [selectedLabels, items])

  const editLabelsPath = useMemo(() => {
    if (!repoNwo || !ssrSafeDocument?.location) return undefined
    const baseUrl = ssrSafeDocument.location.origin
    return `${baseUrl}/${repoNwo}${URLS.labelPageSuffix}`
  }, [repoNwo])

  const onFooterClick = useCallback(
    (e: React.MouseEvent) => {
      if (ssrSafeDocument?.location?.href && showEditLabelsButton && editLabelsPath) {
        // if the mouse wheel is clicked, do nothing
        if (e.button === 1) return

        // if a keyboard modifier is used, do nothing as the underlying default logic will
        // already open this into a new tab.
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

        e.preventDefault()
        ssrSafeDocument.location.href = editLabelsPath
      }
    },
    [editLabelsPath, showEditLabelsButton],
  )

  const footer: React.ReactElement | undefined = useMemo(() => {
    if (!showEditLabelsButton || !editLabelsPath) {
      return undefined
    }

    return (
      <Box sx={{display: 'flex', flexGrow: 1, justifyContent: 'center'}}>
        <LinkButton
          variant="invisible"
          sx={{fontWeight: 'bold', fontSize: 0}}
          onClick={onFooterClick}
          href={editLabelsPath}
          size={'small'}
        >
          {LABELS.editLabels}
        </LinkButton>
      </Box>
    )
  }, [editLabelsPath, onFooterClick, showEditLabelsButton])

  return (
    <Box sx={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1}}>
      <ItemPicker
        loading={isLoading || isServerFetching}
        items={items}
        initialSelectedItems={selectedLabels}
        openHotkey={shortcutEnabled ? HOTKEYS.labelPicker : undefined}
        title={title || LABELS.labelsHeader}
        filterItems={filterItems}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        placeholderText={`Filter labels ${
          totalNumberOfLabels >= VALUES.labelsMaxPreloadCount ? `(${VALUES.labelsMaxPreloadCount} labels loaded)` : ''
        }`}
        selectionVariant="multiple"
        onSelectionChange={onSelectionChanged}
        renderAnchor={anchorProps => anchorElement(anchorProps)}
        groups={groups}
        insidePortal={insidePortal}
        height={'large'}
        width={'medium'}
        nested={nested}
        resultListAriaLabel={'Label results'}
        triggerOpen={triggerOpen}
        initialFilter={initialFilter}
        selectPanelRef={labelPickerRef}
        footer={footer}
      />
    </Box>
  )
}

export function DefaultLabelAnchor({
  labels,
  readonly,
  shortcutEnabled,
  shortcutHintVisible,
  anchorProps,
}: Pick<LabelPickerProps, 'labels' | 'readonly' | 'shortcutEnabled' | 'shortcutHintVisible'> & {
  anchorProps?: React.HTMLAttributes<HTMLElement> | undefined
}) {
  return (
    <CompressedLabelAnchor
      labels={labels}
      readonly={readonly}
      displayHotkey={shortcutEnabled && (shortcutHintVisible || false)}
      anchorProps={readonly ? undefined : anchorProps}
    />
  )
}

try{ BulkIssuesLabelPicker.displayName ||= 'BulkIssuesLabelPicker' } catch {}
try{ LabelPicker.displayName ||= 'LabelPicker' } catch {}
try{ ItemPickerFetcher.displayName ||= 'ItemPickerFetcher' } catch {}
try{ ItemPickerWrapper.displayName ||= 'ItemPickerWrapper' } catch {}
try{ DefaultLabelAnchor.displayName ||= 'DefaultLabelAnchor' } catch {}